import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  extractInsIdsFromText,
  isCompactLegacyInsId,
  replaceInsIdsInText,
} from '../../utils/ins-id.util';
import { RegulationReferenceMappingService } from './regulation-reference-mapping.service';

/** Tenant `0` is the unscope/default tenant and must not trigger mapping. */
const UNSCOPED_TENANT_ID = '0';

const CITATION_INS_ID_FIELDS = [
  'insight_id',
  'id',
  'ins_id',
  'reference_insight_id',
] as const;

const CITATION_URL_FIELDS = ['ref_url', 'ref_urls'] as const;

type AnswerLike = {
  readonly text?: unknown;
  readonly metadata?: unknown;
};

/**
 * Rewrites compact INS tokens in persisted answer content (prose and regulations
 * citations) to tenant regulation ids so history and share views keep mapped values.
 * Mapping failures leave the original answer unchanged so save still proceeds.
 */
export function applyTenantInsIdsToAnswer<T extends AnswerLike>(
  answer: T,
  tenantId: string,
  mappingService: Pick<RegulationReferenceMappingService, 'getMappedRegulationId'>,
  accessToken: string
): Observable<T> {
  const normalizedTenantId = (tenantId || '').trim();
  if (!normalizedTenantId || normalizedTenantId === UNSCOPED_TENANT_ID || accessToken.length === 0) {
    return of(answer);
  }

  const insIds = collectCompactInsIdsFromAnswer(answer);
  if (insIds.length === 0) {
    return of(answer);
  }

  return forkJoin(
    insIds.map((insId) =>
      mappingService.getMappedRegulationId(insId, insId, normalizedTenantId).pipe(
        map((mappedId) => [insId, (mappedId || insId).toUpperCase()] as const)
      )
    )
  ).pipe(
    map((entries) => {
      const mappings = new Map(entries);
      const remapped = remapAnswerInsIds(answer, mappings);
      return remapped.changed ? ({ ...answer, ...remapped.fields } as T) : answer;
    }),
    catchError(() => of(answer))
  );
}

function collectCompactInsIdsFromAnswer(answer: AnswerLike): string[] {
  const ids = new Set<string>();
  const text = typeof answer.text === 'string' ? answer.text : '';
  extractInsIdsFromText(text).forEach((insId) => ids.add(insId));
  collectCompactInsIdsFromCitations(getAnswerMetadataCitations(answer.metadata)).forEach((insId) =>
    ids.add(insId)
  );
  return Array.from(ids);
}

function getAnswerMetadataCitations(metadata: unknown): unknown {
  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) {
    return undefined;
  }
  return (metadata as Record<string, unknown>)['citations'];
}

function collectCompactInsIdsFromCitations(citations: unknown): string[] {
  if (!Array.isArray(citations)) {
    return [];
  }

  const ids = new Set<string>();
  for (const citation of citations) {
    collectCompactInsIdsFromCitationRecord(citation).forEach((insId) => ids.add(insId));
  }
  return Array.from(ids);
}

function collectCompactInsIdsFromCitationRecord(citation: unknown): string[] {
  if (!citation || typeof citation !== 'object' || Array.isArray(citation)) {
    return [];
  }

  const record = citation as Record<string, unknown>;
  const ids: string[] = [];
  for (const field of CITATION_INS_ID_FIELDS) {
    addCompactInsIdFromField(record[field], ids);
  }

  const nestedMetadata = record['metadata'];
  if (nestedMetadata && typeof nestedMetadata === 'object' && !Array.isArray(nestedMetadata)) {
    addCompactInsIdFromField(
      (nestedMetadata as Record<string, unknown>)['reference_insight_id'],
      ids
    );
  }

  for (const urlField of CITATION_URL_FIELDS) {
    collectCompactInsIdsFromUrlField(record[urlField]).forEach((insId) => ids.push(insId));
  }

  return ids;
}

function addCompactInsIdFromField(value: unknown, into: string[]): void {
  if (typeof value !== 'string') {
    return;
  }
  const trimmed = value.trim();
  if (isCompactLegacyInsId(trimmed)) {
    into.push(trimmed.toUpperCase());
  }
}

function collectCompactInsIdsFromUrlField(value: unknown): string[] {
  if (typeof value === 'string') {
    return [...extractInsIdsFromText(value)];
  }
  if (!Array.isArray(value)) {
    return [];
  }

  const ids = new Set<string>();
  for (const entry of value) {
    if (typeof entry === 'string') {
      extractInsIdsFromText(entry).forEach((insId) => ids.add(insId));
    }
  }
  return Array.from(ids);
}

function remapAnswerInsIds(
  answer: AnswerLike,
  mappings: ReadonlyMap<string, string>
): { changed: boolean; fields: { text?: string; metadata?: unknown } } {
  const fields: { text?: string; metadata?: unknown } = {};
  let changed = false;

  const text = typeof answer.text === 'string' ? answer.text : '';
  const remappedText = replaceInsIdsInText(text, mappings);
  if (remappedText !== text) {
    fields.text = remappedText;
    changed = true;
  }

  const metadataRemap = remapAnswerMetadata(answer.metadata, mappings);
  if (metadataRemap.changed) {
    fields.metadata = metadataRemap.metadata;
    changed = true;
  }

  return { changed, fields };
}

function remapAnswerMetadata(
  metadata: unknown,
  mappings: ReadonlyMap<string, string>
): { changed: boolean; metadata: unknown } {
  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) {
    return { changed: false, metadata };
  }

  const record = metadata as Record<string, unknown>;
  const citations = record['citations'];
  if (!Array.isArray(citations)) {
    return { changed: false, metadata };
  }

  let citationsChanged = false;
  const remappedCitations = citations.map((citation) => {
    const remapped = remapCitationRecord(citation, mappings);
    if (remapped !== citation) {
      citationsChanged = true;
    }
    return remapped;
  });

  if (!citationsChanged) {
    return { changed: false, metadata };
  }

  return {
    changed: true,
    metadata: { ...record, citations: remappedCitations },
  };
}

function remapCitationRecord(
  citation: unknown,
  mappings: ReadonlyMap<string, string>
): unknown {
  if (!citation || typeof citation !== 'object' || Array.isArray(citation)) {
    return citation;
  }

  const record = citation as Record<string, unknown>;
  let changed = false;
  const next: Record<string, unknown> = { ...record };

  for (const field of CITATION_INS_ID_FIELDS) {
    const value = next[field];
    if (typeof value === 'string') {
      const mapped = mapCompactInsFieldValue(value, mappings);
      if (mapped !== value) {
        next[field] = mapped;
        changed = true;
      }
    }
  }

  const nestedMetadata = next['metadata'];
  if (nestedMetadata && typeof nestedMetadata === 'object' && !Array.isArray(nestedMetadata)) {
    const metaRecord = { ...(nestedMetadata as Record<string, unknown>) };
    const referenceId = metaRecord['reference_insight_id'];
    if (typeof referenceId === 'string') {
      const mapped = mapCompactInsFieldValue(referenceId, mappings);
      if (mapped !== referenceId) {
        metaRecord['reference_insight_id'] = mapped;
        next['metadata'] = metaRecord;
        changed = true;
      }
    }
  }

  for (const urlField of CITATION_URL_FIELDS) {
    const remappedUrls = remapCitationUrlField(next[urlField], mappings);
    if (remappedUrls.changed) {
      next[urlField] = remappedUrls.value;
      changed = true;
    }
  }

  return changed ? next : citation;
}

function mapCompactInsFieldValue(value: string, mappings: ReadonlyMap<string, string>): string {
  const trimmed = value.trim();
  if (!isCompactLegacyInsId(trimmed)) {
    return value;
  }
  return mappings.get(trimmed.toUpperCase()) ?? value;
}

function remapCitationUrlField(
  value: unknown,
  mappings: ReadonlyMap<string, string>
): { changed: boolean; value: unknown } {
  if (typeof value === 'string') {
    const remapped = replaceInsIdsInText(value, mappings);
    return { changed: remapped !== value, value: remapped };
  }
  if (!Array.isArray(value)) {
    return { changed: false, value };
  }

  let changed = false;
  const remapped = value.map((entry) => {
    if (typeof entry !== 'string') {
      return entry;
    }
    const next = replaceInsIdsInText(entry, mappings);
    if (next !== entry) {
      changed = true;
    }
    return next;
  });
  return { changed, value: remapped };
}
